import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import type { SxProps, Theme } from '@mui/material/styles'
import { Check, ChevronDown, X } from 'lucide-react'
import { useI18n } from '@/core/i18n/useI18n'
import { useDeclarativeText } from '../i18n/declarativeText'
import { LEAF_RADIUS_SM } from '../../styles/theme'
import { normalizeSearchText, optionMatches } from './selectSearch'
import { selectTokens, selectWidth, type SelectControlSize, type SelectControlWidth } from './selectTokens'

export interface SelectOption {
  /** ID real enviado ao backend. String vazia é reservada ao estado sem filtro. */
  value: string
  label: string
  /** Rótulo compacto no controle quando difere do texto explicativo do menu. */
  displayLabel?: string
  /** Conteúdo adicional pesquisável sem poluir o rótulo principal. */
  keywords?: string[]
  /** Segunda linha opcional para desambiguar registros homônimos. */
  sublabel?: string
  /** Metadado curto alinhado à direita (placa, código etc.). */
  hint?: string
  /** Miniatura à esquerda do rótulo, na lista e no controle fechado. */
  icon?: ReactNode
  disabled?: boolean
  reason?: string
}

export interface SelectLoadRequest {
  query: string
  page: number
  pageSize: number
  signal: AbortSignal
}

export interface SelectOptionsPage {
  items: SelectOption[]
  page: number
  pages: number
  total?: number
}

export type SelectOptionsLoader = (request: SelectLoadRequest) => Promise<SelectOptionsPage>

export interface SearchableSelectProps {
  id?: string
  name?: string
  label?: string
  /** Nome acessível quando o layout não exibe uma label visual. */
  ariaLabel?: string
  value: string | string[]
  onChange: (value: string | string[]) => void
  options?: SelectOption[]
  /** Busca server-side paginada. Quando presente, o filtro local é desativado. */
  loadOptions?: SelectOptionsLoader
  /** Invalida cache/resultados quando tenant, cliente ou outra dependência muda. */
  asyncKey?: string
  /** Mantém rótulos de IDs selecionados que não estejam na página remota atual. */
  selectedOptions?: SelectOption[]
  multiple?: boolean
  labelId?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyOption?: string
  noOptionsText?: string
  loadingText?: string
  helperText?: ReactNode
  error?: boolean | string
  loading?: boolean
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  clearable?: boolean
  sizeVariant?: SelectControlSize
  width?: SelectControlWidth
  debounceMs?: number
  pageSize?: number
  autoFocus?: boolean
  autoOpen?: boolean
  onBlur?: () => void
  onClose?: (reason: string) => void
  renderOption?: (option: SelectOption, selected: boolean) => ReactNode
  sx?: SxProps<Theme>
}

function uniqueOptions(options: SelectOption[]): SelectOption[] {
  const seen = new Set<string>()
  return options.filter((option) => {
    if (seen.has(option.value)) return false
    seen.add(option.value)
    return true
  })
}

function selectedLabel(value: string | string[], options: SelectOption[]): string {
  if (Array.isArray(value)) return ''
  const selected = options.find((option) => option.value === value)
  return selected?.displayLabel ?? selected?.label ?? ''
}

/**
 * Combobox oficial de filtros, formulários, modais e grades.
 *
 * O campo editável é o cabeçalho fixo da lista: ao abrir, o valor exibido dá
 * lugar à busca e permanece visível enquanto somente as opções rolam. O Popper
 * usa portal + flip/preventOverflow do MUI, sem alterar o layout do container.
 */
export function SearchableSelect({
  id,
  name,
  label,
  ariaLabel,
  value,
  onChange,
  options = [],
  loadOptions,
  asyncKey = 'default',
  selectedOptions = [],
  multiple = false,
  labelId,
  placeholder,
  searchPlaceholder,
  emptyOption,
  noOptionsText,
  loadingText,
  helperText,
  error,
  loading: externalLoading = false,
  required = false,
  disabled = false,
  readOnly = false,
  clearable = true,
  sizeVariant = 'standard',
  width = 'full',
  debounceMs = selectTokens.debounceMs,
  pageSize = selectTokens.pageSize,
  autoFocus = false,
  autoOpen = false,
  onBlur,
  onClose,
  renderOption,
  sx,
}: SearchableSelectProps) {
  const { t } = useI18n()
  const tt = useDeclarativeText()
  const [open, setOpen] = useState(autoOpen)
  const [inputValue, setInputValue] = useState('')
  const [remoteOptions, setRemoteOptions] = useState<SelectOption[]>([])
  const [remotePage, setRemotePage] = useState(0)
  const [remotePages, setRemotePages] = useState(0)
  const [remoteLoading, setRemoteLoading] = useState(false)
  const [remoteError, setRemoteError] = useState<string | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)
  const requestVersion = useRef(0)
  const firstRequest = useRef<AbortController | null>(null)
  const nextRequest = useRef<AbortController | null>(null)
  const loaderRef = useRef(loadOptions)
  const cacheRef = useRef(new Map<string, SelectOptionsPage>())
  loaderRef.current = loadOptions

  const empty = useMemo<SelectOption[]>(
    () => (!multiple && emptyOption != null ? [{ value: '', label: emptyOption }] : []),
    [emptyOption, multiple],
  )
  const sourceOptions = loadOptions ? remoteOptions : options
  const remoteIds = useMemo(() => new Set(remoteOptions.map((option) => option.value)), [remoteOptions])
  const allOptions = useMemo(
    () => uniqueOptions([...empty, ...selectedOptions, ...sourceOptions]),
    [empty, selectedOptions, sourceOptions],
  )
  const currentLabel = selectedLabel(value, allOptions)
  const currentTooltipLabel = useMemo(() => {
    if (Array.isArray(value)) return ''
    return allOptions.find((option) => option.value === value)?.label ?? currentLabel
  }, [allOptions, currentLabel, value])
  // Miniatura da opção escolhida no controle fechado: sem ela o campo perde a
  // informação visual assim que o menu fecha.
  const currentIcon = useMemo(() => {
    if (Array.isArray(value) || !value) return null
    return allOptions.find((option) => option.value === value)?.icon ?? null
  }, [allOptions, value])

  const selectedValue = useMemo(() => {
    if (multiple) {
      const ids = new Set(Array.isArray(value) ? value : [])
      return allOptions.filter((option) => ids.has(option.value))
    }
    const idValue = Array.isArray(value) ? '' : value
    return allOptions.find((option) => option.value === idValue)
      ?? (idValue ? { value: idValue, label: idValue } : null)
  }, [allOptions, multiple, value])

  useEffect(() => {
    cacheRef.current.clear()
    setRemoteOptions([])
    setRemotePage(0)
    setRemotePages(0)
    setRemoteError(null)
    firstRequest.current?.abort()
    nextRequest.current?.abort()
    requestVersion.current += 1
  }, [asyncKey])

  useEffect(() => {
    if (!open) setInputValue(currentLabel)
  }, [currentLabel, open])

  const requestPage = useCallback(async (page: number, append: boolean, version: number, signal: AbortSignal) => {
    const loader = loaderRef.current
    if (!loader) return
    const query = inputValue.trim().replace(/\s+/g, ' ')
    const cacheKey = `${asyncKey}\u0000${normalizeSearchText(query)}\u0000${page}`
    try {
      const cached = cacheRef.current.get(cacheKey)
      const result = cached ?? await loader({ query, page, pageSize, signal })
      if (!cached) cacheRef.current.set(cacheKey, result)
      if (signal.aborted || version !== requestVersion.current) return
      setRemoteOptions((previous) => uniqueOptions(append ? [...previous, ...result.items] : result.items))
      setRemotePage(result.page)
      setRemotePages(result.pages)
      setRemoteError(null)
    } catch {
      if (!signal.aborted && version === requestVersion.current) {
        setRemoteError(t('form.searchableSelect.loadError'))
        if (!append) setRemoteOptions([])
      }
    } finally {
      if (!signal.aborted && version === requestVersion.current) setRemoteLoading(false)
    }
  }, [asyncKey, inputValue, pageSize, t])

  // Nova busca: debounce, cancelamento e versão impedem respostas antigas de vencer.
  useEffect(() => {
    if (!open || !loadOptions) return
    const version = ++requestVersion.current
    firstRequest.current?.abort()
    nextRequest.current?.abort()
    const controller = new AbortController()
    firstRequest.current = controller
    setRemoteOptions([])
    setRemotePage(0)
    setRemotePages(0)
    setRemoteLoading(true)
    setRemoteError(null)
    const timer = window.setTimeout(
      () => void requestPage(1, false, version, controller.signal),
      debounceMs,
    )
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [open, inputValue, loadOptions, asyncKey, debounceMs, retryNonce, requestPage])

  useEffect(() => () => {
    firstRequest.current?.abort()
    nextRequest.current?.abort()
  }, [])

  const loadNextPage = useCallback(() => {
    if (!loadOptions || remoteLoading || remoteError || remotePage >= remotePages) return
    const controller = new AbortController()
    nextRequest.current?.abort()
    nextRequest.current = controller
    const version = requestVersion.current
    setRemoteLoading(true)
    void requestPage(remotePage + 1, true, version, controller.signal)
  }, [loadOptions, remoteError, remoteLoading, remotePage, remotePages, requestPage])

  // Largura do campo (âncora) medida ao vivo: o menu nunca fica mais estreito que o
  // controle, mas pode CRESCER até a largura do conteúdo (nada de rótulo cortado).
  const rootRef = useRef<HTMLDivElement>(null)
  const [anchorWidth, setAnchorWidth] = useState(0)
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const measure = () => setAnchorWidth(el.getBoundingClientRect().width)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const isLoading = externalLoading || remoteLoading
  const errorMessage = typeof error === 'string' ? error : remoteError
  const hasError = Boolean(error || remoteError)
  const hasSelection = Array.isArray(value) ? value.length > 0 : Boolean(value)
  const inputHeight = selectTokens.height[sizeVariant]
  const popupMaxHeight = `min(${selectTokens.menu.maxHeight}px, calc(100dvh - ${selectTokens.menu.viewportGutter * 2 + inputHeight}px))`
  const resolvedNoOptionsText = noOptionsText ?? t('form.searchableSelect.noOptions')
  const resolvedLoadingText = loadingText ?? t('form.searchableSelect.searching')
  const contextualSearch = searchPlaceholder ?? t('form.searchableSelect.searchField', { field: label?.toLocaleLowerCase() || t('form.searchableSelect.optionFallback') })

  function clearInput(event: React.MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    setInputValue('')
  }

  function clearSelection(event: React.MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    onChange(multiple ? [] : '')
    setInputValue('')
  }

  return (
    // maxWidth:100% impede o controle de estourar a barra de filtros quando a
    // largura da escala (132–360 px) é maior que o espaço disponível — o caso do
    // zoom alto do navegador, em que a viewport encolhe mas a escala não.
    <Box ref={rootRef} sx={{ width: selectWidth(width), maxWidth: '100%', minWidth: 0, position: 'relative', ...sx }}>
      <Autocomplete<SelectOption, boolean, false, false>
        id={id}
        multiple={multiple}
        options={allOptions}
        value={selectedValue}
        open={open}
        inputValue={inputValue}
        disabled={disabled}
        readOnly={readOnly}
        loading={isLoading}
        disableCloseOnSelect={multiple}
        openOnFocus
        autoHighlight
        handleHomeEndKeys
        clearOnBlur={false}
        filterOptions={loadOptions
          ? (items, state) => state.inputValue
            ? items.filter((item) => item.value !== '' && remoteIds.has(item.value))
            : items
          : (items, state) => items.filter((item) => optionMatches(item, state.inputValue))}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => Boolean(option.disabled)}
        isOptionEqualToValue={(option, selected) => option.value === selected.value}
        onOpen={() => {
          if (readOnly) return
          setOpen(true)
          setInputValue('')
        }}
        onClose={(_event, reason) => {
          setOpen(false)
          setInputValue(currentLabel)
          onClose?.(reason)
        }}
        onInputChange={(_event, next, reason) => {
          if (reason === 'input' || reason === 'clear') setInputValue(next)
        }}
        onChange={(_event, next) => {
          if (multiple) {
            const selected = Array.isArray(next) ? next : []
            onChange(selected.map((option) => option.value))
            setInputValue('')
          } else {
            const selected = Array.isArray(next) ? null : next
            onChange(selected?.value ?? '')
            setInputValue(selected?.displayLabel ?? selected?.label ?? '')
          }
        }}
        noOptionsText={remoteError ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 0.5 }}>
            <Box component="span">{remoteError}</Box>
            <Button size="small" onClick={() => setRetryNonce((nonce) => nonce + 1)}>{t('common.retry')}</Button>
          </Box>
        ) : resolvedNoOptionsText}
        loadingText={resolvedLoadingText}
        openText={t('form.searchableSelect.openOptions')}
        closeText={t('form.searchableSelect.closeOptions')}
        popupIcon={<ChevronDown size={16} aria-hidden="true" />}
        renderTags={(selected, getTagProps) => {
          const first = selected[0]
          if (!first) return null
          const { key, ...tagProps } = getTagProps({ index: 0 })
          const remainingLabels = selected.slice(1).map((option) => option.label).join(', ')
          return (
            <>
              <Tooltip key={key} title={first.label} arrow enterDelay={350} disableInteractive describeChild>
                <Chip
                  {...tagProps}
                  size="small"
                  label={first.label}
                  sx={{ maxWidth: selected.length > 1 ? 'calc(100% - 76px)' : 'calc(100% - 28px)', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                />
              </Tooltip>
              {selected.length > 1 && (
                <Tooltip title={remainingLabels} arrow enterDelay={350} disableInteractive describeChild>
                  <Chip
                    size="small"
                    label={`+${selected.length - 1}`}
                    aria-label={t('form.searchableSelect.moreSelected', { count: selected.length - 1 })}
                  />
                </Tooltip>
              )}
            </>
          )
        }}
        renderInput={(params) => (
          <Tooltip title={!open && !multiple ? currentTooltipLabel : ''} arrow enterDelay={350} disableInteractive describeChild>
            <TextField
              {...params}
              name={name}
              label={label}
              required={required}
              autoFocus={autoFocus}
              size="small"
              error={hasError}
              helperText={errorMessage ?? helperText}
              placeholder={open ? contextualSearch : placeholder}
              onBlur={onBlur}
              inputProps={{
                ...params.inputProps,
                'aria-labelledby': labelId,
                'aria-label': label ? undefined : ariaLabel ?? placeholder ?? t('form.searchableSelect.selectOption'),
                autoComplete: 'off',
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: !open && currentIcon
                  ? <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.75, ml: 0.25 }}>{currentIcon}</Box>
                  : params.InputProps.startAdornment,
                endAdornment: (
                  <>
                    {isLoading && <CircularProgress color="inherit" size={15} aria-hidden="true" />}
                    {open && inputValue && (
                      <IconButton size="small" aria-label={t('form.searchableSelect.clearSearch')} onMouseDown={(event) => event.preventDefault()} onClick={clearInput} sx={{ p: 0.35 }}>
                        <X size={14} aria-hidden="true" />
                      </IconButton>
                    )}
                    {/* `disabled` conta junto com `readOnly`: sem isso um campo
                        travado ainda oferecia o botão de limpar, e ele limpava. */}
                    {!open && clearable && !required && hasSelection && !readOnly && !disabled && (
                      <IconButton size="small" aria-label={t('form.searchableSelect.clearSelection')} onMouseDown={(event) => event.preventDefault()} onClick={clearSelection} sx={{ p: 0.35 }}>
                        <X size={14} aria-hidden="true" />
                      </IconButton>
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          </Tooltip>
        )}
        renderOption={(props, option, state) => {
          const { key, ...optionProps } = props
          const secondary = option.reason ?? option.sublabel
          const tooltipText = [
            option.label,
            secondary ? tt(secondary) : secondary,
            option.hint ? tt(option.hint) : option.hint,
          ].filter(Boolean).join(' · ')
          return (
            <Box
              component="li"
              key={key}
              {...optionProps}
              sx={{
                minHeight: option.sublabel || option.reason ? 48 : selectTokens.menu.optionHeight,
                gap: 1,
                minWidth: 0,
                contentVisibility: 'auto',
                containIntrinsicSize: `${selectTokens.menu.optionHeight}px`,
                opacity: option.disabled ? 0.68 : 1,
                '&[aria-selected="true"]': { bgcolor: 'primary.50' },
                '&.Mui-focused': { bgcolor: 'action.hover' },
              }}
            >
              <Tooltip
                title={tooltipText}
                arrow
                placement="right"
                enterDelay={350}
                disableInteractive
                describeChild
                slotProps={{ tooltip: { sx: { maxWidth: 360, overflowWrap: 'anywhere' } } }}
              >
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                  {renderOption ? renderOption(option, state.selected) : (
                    <>
                      {option.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: state.selected ? 700 : 500 }}>
                          {option.label}
                        </Box>
                        {(option.sublabel || option.reason) && (
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: 'text.secondary' }}>
                            {secondary ? tt(secondary) : secondary}
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Tooltip>
              {option.hint && <Box component="span" sx={{ flexShrink: 0, fontSize: 11, color: 'text.secondary' }}>{tt(option.hint)}</Box>}
              {state.selected && <Check size={15} aria-hidden="true" />}
            </Box>
          )
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              borderRadius: LEAF_RADIUS_SM,
              overflow: 'hidden',
              maxWidth: 'calc(100vw - 24px)',
            },
          },
          popper: {
            placement: 'bottom-start',
            modifiers: [
              { name: 'flip', enabled: true, options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] } },
              { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', padding: selectTokens.menu.viewportGutter, altAxis: true } },
            ],
            sx: {
              zIndex: (theme) => theme.zIndex.modal + 1,
              // `max-content` cresce até o item mais largo (o !important vence o
              // width inline que o MUI fixa igual ao campo). O piso é a largura do
              // próprio campo, e o teto respeita a viewport (24px de folga).
              width: 'max-content !important',
              minWidth: `min(${Math.max(Math.round(anchorWidth), selectTokens.width.xs)}px, calc(100vw - 24px))`,
              maxWidth: 'calc(100vw - 24px)',
            },
          },
          listbox: {
            onScroll: (event) => {
              const target = event.currentTarget
              if (target.scrollHeight - target.scrollTop - target.clientHeight < 56) loadNextPage()
            },
            sx: {
              maxHeight: popupMaxHeight,
              overflowY: 'auto',
              overflowX: 'hidden',
              overscrollBehavior: 'contain',
              scrollbarWidth: 'thin',
              p: 0.5,
            },
          },
        }}
        sx={{
          minWidth: 0,
          '& .MuiOutlinedInput-root': {
            height: inputHeight,
            minHeight: inputHeight,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            py: '0 !important',
            pr: '34px !important',
          },
          '& .MuiAutocomplete-input': {
            minWidth: '0 !important',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .MuiAutocomplete-endAdornment': { right: 7 },
          '& .MuiAutocomplete-clearIndicator': { display: 'none' },
          '& .MuiAutocomplete-tag': { flexShrink: 1 },
        }}
      />
      <Box
        component="span"
        role="status"
        aria-live="polite"
        sx={{ position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}
      >
        {isLoading
          ? resolvedLoadingText
          : remoteError
            ? remoteError
            : t('form.searchableSelect.optionsAvailable', { count: allOptions.length })}
      </Box>
    </Box>
  )
}
